<script setup lang="ts">
import { computed, ref } from "vue";
import { Button, Dialog } from "@/design-system";
import type { Lang } from "@/domain/i18n";
import { IMPRESSUM, IMPRESSUM_PLACEHOLDER, LEGAL_LABELS, PRIVACY } from "./legal-content";

/**
 * Footer links to the Impressum and the privacy notice (O-5 b and c).
 *
 * Shown in a Dialog rather than on a route: there is no router in the app, and
 * a sheet keeps the single-screen flow intact. The Impressum still contains
 * placeholders -- they are rendered visibly rather than hidden, so an unfilled
 * page cannot be mistaken for a finished one.
 */
const props = defineProps<{ lang: Lang }>();

const open = ref<"impressum" | "privacy" | null>(null);
const labels = computed(() => LEGAL_LABELS[props.lang]);
const doc = computed(() =>
  open.value === "impressum" ? IMPRESSUM : PRIVACY[props.lang],
);
const isPlaceholder = (line: string) => line.includes(IMPRESSUM_PLACEHOLDER);

/** So "which version am I running?" has an answer on a home-screen icon. */
const build = __BUILD__;
</script>

<template>
  <footer class="legal">
    <button
      type="button"
      class="legal__link"
      data-testid="open-impressum"
      @click="open = 'impressum'"
    >
      {{ labels.impressum }}
    </button>
    <span
      class="legal__sep"
      aria-hidden="true"
    >·</span>
    <button
      type="button"
      class="legal__link"
      data-testid="open-privacy"
      @click="open = 'privacy'"
    >
      {{ labels.privacy }}
    </button>
  </footer>

  <Dialog
    :open="open !== null"
    :title="doc.title"
    data-testid="legal-dialog"
    @close="open = null"
  >
    <div class="legal__doc">
      <section
        v-for="s in doc.sections"
        :key="s.heading"
      >
        <h3 class="legal__heading">
          {{ s.heading }}
        </h3>
        <p
          v-for="(line, i) in s.body"
          :key="i"
          class="legal__body"
          :class="{ 'legal__body--todo': isPlaceholder(line) }"
          :data-todo="isPlaceholder(line) || undefined"
        >
          {{ line }}
        </p>
      </section>
      <p class="legal__updated">
        {{ doc.updated }} · Build {{ build }}
      </p>
    </div>
    <template #footer>
      <Button
        block
        size="lg"
        @click="open = null"
      >
        {{ labels.close }}
      </Button>
    </template>
  </Dialog>
</template>

<style scoped>
.legal {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  padding-top: var(--space-7);
  margin-top: var(--space-3);
  border-top: 1px solid var(--border-subtle);
}
.legal__link {
  border: 0;
  background: none;
  /* A real 44px target (NFR-2) rather than a pseudo-element expander -- these
     are the only two links on the page, so the height costs nothing. */
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  padding: var(--space-3) var(--space-2);
  cursor: pointer;
  font: var(--type-body-sm);
  color: var(--text-muted);
  text-decoration: underline;
  text-underline-offset: 3px;
}
.legal__link:hover {
  color: var(--text-body);
}
.legal__link:focus-visible {
  outline: none;
  box-shadow: var(--ring-focus);
  border-radius: var(--radius-sm);
}
.legal__sep {
  color: var(--text-faint);
}
.legal__doc {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  max-height: 60vh;
  overflow-y: auto;
}
.legal__heading {
  margin: 0 0 var(--space-3);
  font: var(--type-label);
  letter-spacing: var(--tracking-label);
  text-transform: uppercase;
  color: var(--text-muted);
}
.legal__body {
  margin: 0 0 var(--space-3);
  font: var(--type-body-sm);
  color: var(--text-body);
}
/* An unfilled Impressum must look unfilled. */
.legal__body--todo {
  color: var(--danger);
  font-family: var(--font-mono);
  font-size: var(--text-xs);
}
.legal__updated {
  margin: 0;
  font: var(--type-unit);
  text-transform: uppercase;
  letter-spacing: var(--tracking-label);
  color: var(--text-faint);
}
</style>
